import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  GetDemoDomainsWT,
  DeleteDemoDomainWT
} from '../../../redux/actions/watchTower/certifyDemoDomainActions';

import DemoCertifyDomainsModal from './DemoCertifyDomainsModal';

import global from "../../../global.scss";

const DemoCertifyDomainsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.watchTower);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [domainsList, setDomainsList] = useState([]);
  const [domainsTotal, setDomainsTotal] = useState(0);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Domain / Email", sort: "domain" },
    { label: "Status", sort: "status" },
    { label: "Note", sort: "note" },
    { label: "Time stamps", sort: "createdAt" },
    { label: "", sort: null }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetDomains();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setDomainsList(state.demoCertifyDomainsList.records || []);
      setDomainsTotal(state.demoCertifyDomainsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetDomains() {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    return dispatch(GetDemoDomainsWT(page + 1, rowsPerPage, query, setLoading));
  };

  const handleDelete = () => {
    if (!selectedRow) return;
    dispatch(DeleteDemoDomainWT(selectedRow.id, setDeleteLoading)).then(() => {
      setDeleteConfirmOpen(false);
      setSelectedRow(null);
      handleGetDomains();
    });
  };

  return (
    <Box>

      <DemoCertifyDomainsModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetDomains}
        />

      <ConfirmationModal
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title={`Delete "${selectedRow?.domain || ''}"`}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"Demo Access Domains"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Add Domain"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add Domain");
          setModalOpen(true);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          domainsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No domains found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                domainsList.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.domain || <NA />}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: row.status === 'BLOCKED' ? "#B42318" : "#2e7d32",
                          backgroundColor: row.status === 'BLOCKED' ? "#FEF3F2" : "#ECFDF3",
                        }}>
                        {row.status || <NA />}
                      </Box>
                    </TableCell>
                    <TableCell>{row.note || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setSelectedRow(row);
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={domainsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + (selectedRow?.domain || ""));
            setUpdate({
              ...selectedRow,
              defaultMode: 2
            });
            setMenuOpen(false);
            setModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            setDeleteConfirmOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}
            sx={{ color: "#B42318" }}>
            <DeleteOutlineIcon sx={{ color: "#B42318" }} />
            Delete
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default DemoCertifyDomainsPage;
